import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Button,
} from '@mui/material';
import {
  History as HistoryIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import ProductCard from './ProductCard';
import { personalizationService } from '../services/personalization';

const SmartRecommendations = ({
  currentProduct,
  products,
  title = "Recommended for You",
  showFilters = true,
  loading: externalLoading = false,
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [internalLoading, setInternalLoading] = useState(true);
  const [, setGreeting] = useState('');

  // Use external loading if provided, otherwise use internal
  const loading = externalLoading || internalLoading;

  useEffect(() => {
    setInternalLoading(true);

    // Simulate loading delay for better UX
    setTimeout(() => {
      // Use products if available, otherwise get smart recommendations
      if (products && products.length > 0) {
        // If we have products from the API, use them directly
        setRecommendations(products.slice(0, 8));
      } else {
        // Fallback to smart recommendations from personalization service
        const recs = personalizationService.getSmartRecommendations(currentProduct, products);
        setRecommendations(recs.slice(0, 8));
      }
      setGreeting(personalizationService.getPersonalizedGreeting());
      setInternalLoading(false);
    }, 500);
  }, [currentProduct, products, activeFilter]);

  const filterRecommendations = () => {
    let filtered = [...recommendations];

    switch (activeFilter) {
      case 'trending':
        // Sort by popularity (you could add a popularity field to products)
        filtered = filtered.sort(() => Math.random() - 0.5).slice(0, 6);
        break;
      case 'seasonal':
        // Filter by current season
        const season = new Date().getMonth() >= 3 && new Date().getMonth() <= 9 ? 'summer' : 'winter';
        filtered = filtered.filter(p => {
          const desc = (p.description || '').toLowerCase();
          return season === 'summer'
            ? desc.includes('light') || desc.includes('breathable')
            : desc.includes('warm') || desc.includes('winter');
        });
        break;
      case 'location':
        // Location-based filtering (Kenya-specific)
        const locationContent = personalizationService.getLocationBasedContent();
        filtered = filtered.filter(p => {
          return locationContent.popularCategories.some(cat =>
            p.name.toLowerCase().includes(cat.toLowerCase())
          );
        });
        break;
      case 'recent':
        // Get recently viewed products
        const recentlyViewed = personalizationService.getRecentlyViewed(currentProduct?.id);
        filtered = recentlyViewed.slice(0, 8);
        break;
      default:
        break;
    }

    return filtered.length > 0 ? filtered : recommendations;
  };

  const displayProducts = filterRecommendations();

  // Nothing to recommend — hide the whole section
  if (!loading && recommendations.length === 0) {
    return null;
  }

  return (
    <Box sx={{ py: { xs: 3, md: 4 } }}>
      {/* Header - Compact */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: 'var(--primary)' }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                color: '#1A1A1A',
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>

        {/* Filter Chips - Compact */}
        {showFilters && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              onClick={() => setActiveFilter('all')}
              size="small"
              variant={activeFilter === 'all' ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontSize: '0.8rem',
                backgroundColor: activeFilter === 'all' ? 'var(--primary)' : 'transparent',
                color: activeFilter === 'all' ? 'white' : '#666',
                borderColor: activeFilter === 'all' ? 'var(--primary)' : '#E0E0E0',
                '&:hover': {
                  backgroundColor: activeFilter === 'all' ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  borderColor: 'var(--primary)',
                  color: activeFilter === 'all' ? 'white' : 'var(--primary)',
                },
              }}
            />
            <Chip
              icon={<HistoryIcon sx={{ fontSize: 16 }} />}
              label="Recently Viewed"
              onClick={() => setActiveFilter('recent')}
              size="small"
              variant={activeFilter === 'recent' ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontSize: '0.8rem',
                backgroundColor: activeFilter === 'recent' ? 'var(--primary)' : 'transparent',
                color: activeFilter === 'recent' ? 'white' : '#666',
                borderColor: activeFilter === 'recent' ? 'var(--primary)' : '#E0E0E0',
                '& .MuiChip-icon': {
                  fontSize: 16,
                  color: activeFilter === 'recent' ? 'white' : '#666',
                },
                '&:hover': {
                  backgroundColor: activeFilter === 'recent' ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  borderColor: 'var(--primary)',
                  color: activeFilter === 'recent' ? 'white' : 'var(--primary)',
                  '& .MuiChip-icon': {
                    color: activeFilter === 'recent' ? 'white' : 'var(--primary)',
                  },
                },
              }}
            />
            <Chip
              label="Trending"
              onClick={() => setActiveFilter('trending')}
              size="small"
              variant={activeFilter === 'trending' ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontSize: '0.8rem',
                backgroundColor: activeFilter === 'trending' ? 'var(--primary)' : 'transparent',
                color: activeFilter === 'trending' ? 'white' : '#666',
                borderColor: activeFilter === 'trending' ? 'var(--primary)' : '#E0E0E0',
                '& .MuiChip-icon': {
                  fontSize: 16,
                  color: activeFilter === 'trending' ? 'white' : '#666',
                },
                '&:hover': {
                  backgroundColor: activeFilter === 'trending' ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  borderColor: 'var(--primary)',
                  color: activeFilter === 'trending' ? 'white' : 'var(--primary)',
                  '& .MuiChip-icon': {
                    color: activeFilter === 'trending' ? 'white' : 'var(--primary)',
                  },
                },
              }}
            />
            <Chip
              label="Seasonal"
              onClick={() => setActiveFilter('seasonal')}
              size="small"
              variant={activeFilter === 'seasonal' ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontSize: '0.8rem',
                backgroundColor: activeFilter === 'seasonal' ? 'var(--primary)' : 'transparent',
                color: activeFilter === 'seasonal' ? 'white' : '#666',
                borderColor: activeFilter === 'seasonal' ? 'var(--primary)' : '#E0E0E0',
                '& .MuiChip-icon': {
                  fontSize: 16,
                  color: activeFilter === 'seasonal' ? 'white' : '#666',
                },
                '&:hover': {
                  backgroundColor: activeFilter === 'seasonal' ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  borderColor: 'var(--primary)',
                  color: activeFilter === 'seasonal' ? 'white' : 'var(--primary)',
                  '& .MuiChip-icon': {
                    color: activeFilter === 'seasonal' ? 'white' : 'var(--primary)',
                  },
                },
              }}
            />
            <Chip
              label="Local Favorites"
              onClick={() => setActiveFilter('location')}
              size="small"
              variant={activeFilter === 'location' ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontSize: '0.8rem',
                backgroundColor: activeFilter === 'location' ? 'var(--primary)' : 'transparent',
                color: activeFilter === 'location' ? 'white' : '#666',
                borderColor: activeFilter === 'location' ? 'var(--primary)' : '#E0E0E0',
                '& .MuiChip-icon': {
                  fontSize: 16,
                  color: activeFilter === 'location' ? 'white' : '#666',
                },
                '&:hover': {
                  backgroundColor: activeFilter === 'location' ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  borderColor: 'var(--primary)',
                  color: activeFilter === 'location' ? 'white' : 'var(--primary)',
                  '& .MuiChip-icon': {
                    color: activeFilter === 'location' ? 'white' : 'var(--primary)',
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Products Grid - Compact */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 2 }}>
        {loading ? (
          // Loading skeletons
          Array.from({ length: 6 }).map((_, index) => (
            <Box key={index}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))
        ) : displayProducts.length > 0 ? (
          displayProducts.map((product) => (
            <Box key={product.id} sx={{
              '& .MuiCard-root': {
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }
              },
              '& .MuiTypography-h6': {
                fontSize: '0.9rem',
                lineHeight: 1.3,
                height: '2.6em',
                overflow: 'hidden',
              },
              '& .MuiTypography-body2': {
                fontSize: '0.85rem',
              },
            }}>
              <ProductCard product={product} />
            </Box>
          ))
        ) : (
          // No recommendations fallback
          <Box
            sx={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              py: 8,
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No recommendations available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Browse more products to get personalized recommendations
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 3,
                backgroundColor: 'var(--button-primary)',
                '&:hover': {
                  backgroundColor: 'var(--button-hover)',
                },
              }}
              onClick={() => window.location.href = '/products'}
            >
              Explore Products
            </Button>
          </Box>
        )}
      </Box>

      {/* Load More */}
      {!loading && displayProducts.length >= 8 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            sx={{
              borderColor: 'var(--primary)',
              color: 'var(--primary)',
              px: 4,
              '&:hover': {
                borderColor: 'var(--button-hover)',
                backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
              },
            }}
          >
            Load More Recommendations
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SmartRecommendations;
